import React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, XAxis } from 'recharts';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../ui/card';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '../../ui/chart';
import { OrdersByStatusProps } from './home.types';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import { ORDER_STATUSES } from '../../../constants';
import { getDates } from './home.helpers';

const chartConfig = {
  orders: {
    label: 'Orders',
  },
  completed: {
    label: 'Completed',
    color: 'hsl(var(--chart-1))',
  },
  intransit: {
    label: 'In Transit',
    color: 'hsl(var(--chart-2))',
  },
  expected: {
    label: 'Expected',
    color: 'hsl(var(--chart-3))',
  },
  draft: {
    label: 'Draft',
    color: 'hsl(var(--chart-4))',
  },
  failed: {
    label: 'Failed',
    color: 'hsl(var(--chart-5))',
  },
} satisfies ChartConfig;

export function OrdersByStatus({ ordersByStatus, range }: OrdersByStatusProps & { range: string }) {
  const history = useHistory();

  const chartData = [
    { status: 'Completed', orders: ordersByStatus.completed || 0, fill: 'hsl(var(--chart-1))' },
    { status: 'Next attempt', orders: ordersByStatus.awaitingNextAttempt || 0, fill: 'hsl(var(--chart-7))' },
    { status: 'Out for delivery', orders: ordersByStatus.outForDelivery || 0, fill: 'hsl(var(--chart-6))' },
    { status: 'In Transit', orders: ordersByStatus.inTransit || 0, fill: 'hsl(var(--chart-2))' },
    { status: 'Expected', orders: ordersByStatus.expected || 0, fill: 'hsl(var(--chart-3))' },
    { status: 'Draft', orders: ordersByStatus.draft || 0, fill: 'hsl(var(--chart-4))' },
    { status: 'Failed', orders: ordersByStatus.failed || 0, fill: 'hsl(var(--chart-5))' },
  ];

  const openOrdersPage = (status: string) => {
    const statusMap = (status) => {
      switch (status) {
        case 'Expected':
          return 'EXPECTED';
        case 'Failed':
          return `${ORDER_STATUSES.FAILED},${ORDER_STATUSES.RETURNED}`;
        case 'Next attempt':
          return 'AWAITING_NEXT_ATTEMPT';
        default:
          return status.toUpperCase().replace(/ /g, '_');
      }
    };
    const { from, to } = getDates(range);
    history.push(`/orders?createdAtFrom=${from}&createdAtTo=${to}&statuses=${statusMap(status)}`);
  };

  return (
    <Card className="min-h-[400px]">
      <CardHeader>
        <CardTitle>Orders by Status</CardTitle>
        <CardDescription>{range === 'last-week' ? 'Last week' : 'Last 7 days'}</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig}>
          <BarChart
            accessibilityLayer
            data={chartData}
            margin={{
              top: 30,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis dataKey="status" tickLine={false} tickMargin={10} axisLine={false} />
            <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
            <Bar dataKey="orders" radius={8} onClick={(data) => openOrdersPage(data.status)} cursor="pointer">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
              <LabelList position="top" offset={12} className="fill-foreground" fontSize={12} />
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col items-start gap-2 text-sm">
        <div className="leading-none text-muted-foreground">
          Click a bar to view orders filtered by status for {range === 'last-week' ? 'last week' : 'the last 7 days'}
        </div>
      </CardFooter>
    </Card>
  );
}
